import React, { useState, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import './AdminPanel.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 10;

function AdminPanel() {
  const [pin, setPin] = useState('');
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState('');
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const pinRef = useRef(pin);
  pinRef.current = pin;

  const fetchStats = useCallback(async (p) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/estadisticas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: p })
      });
      if (res.ok) {
        setStats(await res.json());
      }
    } catch (e) { console.error('fetchStats error:', e); }
  }, []);

  const fetchEmails = useCallback(async (p, pg, srch) => {
    setLoading(true);
    try {
      const payload = { pin: p, page: pg, page_size: PAGE_SIZE };
      if (srch) payload.search = srch;
      const res = await fetch(`${API_URL}/api/admin/listar-accesos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const data = await res.json();
        setRows(data.rows || []);
        setTotal(data.total || 0);
      }
    } catch (e) { console.error('fetchEmails error:', e); }
    setLoading(false);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/verificar-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin })
      });
      const data = await res.json();
      if (data.ok) {
        setAuthed(true);
        fetchStats(pin);
        fetchEmails(pin, 1, '');
      } else {
        setAuthError('PIN incorrecto');
      }
    } catch (e) {
      setAuthError('Error de conexión');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchEmails(pin, 1, search);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchEmails(pin, newPage, search);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este registro?')) return;
    setDeleting(id);
    try {
      await fetch(`${API_URL}/api/admin/eliminar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin, id })
      });
      fetchEmails(pin, page, search);
      fetchStats(pin);
    } catch (e) { console.error(e); }
    setDeleting(null);
  };

  const handleExportCSV = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/exportar-csv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin })
      });
      if (res.ok) {
        const data = await res.json();
        const blob = new Blob([data.csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `emails_tecnoabogado_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) { console.error(e); }
  };

  const handleLogout = () => {
    setAuthed(false);
    setPin('');
    setStats(null);
    setRows([]);
    setSearch('');
    setPage(1);
  };

  const totalPages = useMemo(() => Math.ceil(total / PAGE_SIZE), [total]);

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const parseDevice = (ua) => {
    if (!ua) return 'Desconocido';
    if (ua.includes('iPhone')) return 'iPhone';
    if (ua.includes('iPad')) return 'iPad';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Mac')) return 'Mac';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('curl')) return 'curl';
    if (ua.includes('python')) return 'Python';
    return 'Otro';
  };

  // Login screen
  if (!authed) {
    return (
      <div className="admin-login-wrapper" data-testid="admin-login-page">
        <div className="admin-login-card">
          <div className="admin-login-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <h1>Panel de Administración</h1>
          <p className="admin-login-subtitle">Tecnoabogado Anonimizador</p>
          <form onSubmit={handleLogin}>
            <div className="admin-input-group">
              <input
                type="password"
                placeholder="Introduce el PIN"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                autoFocus
                data-testid="admin-pin-input"
              />
            </div>
            {authError && <p className="admin-error" data-testid="admin-login-error">{authError}</p>}
            <button type="submit" className="admin-btn-primary" data-testid="admin-login-btn">
              Acceder
            </button>
          </form>
          <Link to="/" className="admin-back-link" data-testid="admin-back-home">Volver a la herramienta</Link>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="admin-wrapper" data-testid="admin-dashboard">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <h1>Panel Admin</h1>
          <span className="admin-header-badge">Tecnoabogado</span>
        </div>
        <div className="admin-header-right">
          <Link to="/" className="admin-header-link" data-testid="admin-go-home">Ir a la herramienta</Link>
          <button onClick={handleLogout} className="admin-btn-logout" data-testid="admin-logout-btn">
            Cerrar sesión
          </button>
        </div>
      </header>

      <main className="admin-main">
        {/* Stats Cards */}
        {stats && (
          <section className="admin-stats" data-testid="admin-stats-section">
            <div className="stat-card stat-total">
              <span className="stat-number" data-testid="stat-total">{stats.total}</span>
              <span className="stat-label">Total registros</span>
            </div>
            <div className="stat-card stat-today">
              <span className="stat-number" data-testid="stat-today">{stats.hoy}</span>
              <span className="stat-label">Hoy</span>
            </div>
            <div className="stat-card stat-week">
              <span className="stat-number" data-testid="stat-week">{stats.semana}</span>
              <span className="stat-label">Esta semana</span>
            </div>
            <div className="stat-card stat-month">
              <span className="stat-number" data-testid="stat-month">{stats.mes}</span>
              <span className="stat-label">Este mes</span>
            </div>
          </section>
        )}

        {/* Actions Bar */}
        <section className="admin-actions">
          <form onSubmit={handleSearch} className="admin-search-form">
            <input
              type="text"
              placeholder="Buscar por email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="admin-search-input"
              data-testid="admin-search-input"
            />
            <button type="submit" className="admin-btn-search" data-testid="admin-search-btn">Buscar</button>
            {search && (
              <button type="button" className="admin-btn-clear" data-testid="admin-clear-search" onClick={() => { setSearch(''); setPage(1); fetchEmails(pin, 1, ''); }}>
                Limpiar
              </button>
            )}
          </form>
          <button onClick={handleExportCSV} className="admin-btn-export" data-testid="admin-export-csv-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Exportar CSV
          </button>
        </section>

        {/* Table */}
        <section className="admin-table-wrapper" data-testid="admin-table-section">
          {loading ? (
            <div className="admin-loading">Cargando...</div>
          ) : rows.length === 0 ? (
            <div className="admin-empty" data-testid="admin-no-results">
              {search ? 'Sin resultados para esta búsqueda' : 'No hay registros todavía'}
            </div>
          ) : (
            <table className="admin-table" data-testid="admin-emails-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Fecha</th>
                  <th>Dispositivo</th>
                  <th>IP</th>
                  <th>Términos</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`admin-row-${r.id}`}>
                    <td className="td-email">{r.email}</td>
                    <td className="td-date">{formatDate(r.created_at)}</td>
                    <td className="td-device">
                      <span className="device-badge">{parseDevice(r.user_agent)}</span>
                    </td>
                    <td className="td-ip">{r.ip || '-'}</td>
                    <td className="td-terms">
                      {r.acepto_terminos ? (
                        <span className="badge-ok">Sí</span>
                      ) : (
                        <span className="badge-no">No</span>
                      )}
                    </td>
                    <td className="td-actions">
                      <button
                        onClick={() => handleDelete(r.id)}
                        disabled={deleting === r.id}
                        className="admin-btn-delete"
                        data-testid={`admin-delete-${r.id}`}
                        title="Eliminar"
                      >
                        {deleting === r.id ? '...' : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          </svg>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Pagination */}
        {totalPages > 1 && (
          <section className="admin-pagination" data-testid="admin-pagination">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1}
              className="admin-btn-page"
              data-testid="admin-prev-page"
            >
              Anterior
            </button>
            <span className="admin-page-info" data-testid="admin-page-info">
              Página {page} de {totalPages} ({total} registros)
            </span>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages}
              className="admin-btn-page"
              data-testid="admin-next-page"
            >
              Siguiente
            </button>
          </section>
        )}
      </main>
    </div>
  );
}

export default AdminPanel;
